import { useCallback, useEffect, useReducer } from 'react';
import { useOverview } from '@atlassian/jira-router-resources-business-overview-details';
import { useFetchOverviewMeta } from '../../services/overview-meta';
import type { HookState, OverviewMeta } from './types';

const initialState: HookState = { data: undefined, loading: false, error: undefined };

type Action =
	| {
			type: 'init';
	  }
	| {
			type: 'fetch';
			value: OverviewMeta;
	  }
	| {
			type: 'success';
			value: OverviewMeta;
	  }
	| {
			type: 'error';
			value: Error;
	  };

const reducer = (state: HookState, action: Action) => {
	switch (action.type) {
		case 'init':
			return initialState;
		case 'fetch':
			return { ...state, data: action.value, loading: true };
		case 'success':
			return { ...state, data: action.value, loading: false };
		case 'error':
			return { ...state, loading: false, error: action.value };
		default:
			return state;
	}
};

export const useOverviewMeta = () => {
	const { data: overview } = useOverview();
	const fetchOverviewMeta = useFetchOverviewMeta();
	const [{ data, loading, error }, dispatch] = useReducer(reducer, initialState);

	const fetch = useCallback(async () => {
		if (overview === null) {
			return;
		}

		dispatch({
			type: 'fetch',
			value: overview.projects,
		});

		try {
			const response = await fetchOverviewMeta(overview.id);

			dispatch({
				type: 'success',
				value: overview.projects.map((project) => {
					if (!response?.has(String(project.id))) {
						return project;
					}

					return {
						...project,
						lead: response.get(String(project.id)),
					};
				}),
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (err: any) {
			dispatch({ type: 'error', value: err });
		}
	}, [fetchOverviewMeta, overview]);

	useEffect(
		() =>
			// Reset the state when the overview changes.
			dispatch({
				type: 'init',
			}),
		[overview],
	);

	return {
		data,
		error,
		fetch,
		loading,
	};
};
